import React, { useState, useRef, useEffect, useCallback } from 'react';
import classnames from 'classnames/bind';

import { Category } from '@services';
import { Button } from '../baseComponents/Button';
import { SubcategoriesNavbar } from '../SubcategoriesNavbar';

import styles from './CategoriesToolbar.module.scss';
import { Icon } from 'gd-design-library';

const CN = 'categories-toolbar';
const buttonsContainerClassName = `${CN}__buttons-container`;
const arrowButtonsClassName = `${CN}__arrow-buttons`;
const cx = classnames.bind(styles);

interface CategoriesToolbarProps {
  categories: Category[];
  onCategoryClick: (categoryId: string) => Promise<boolean>;
}

export const CategoriesToolbar: React.FC<CategoriesToolbarProps> = ({
  categories,
  onCategoryClick
}) => {
  const [ isCategoriesHovered, setIsCategoriesHovered ] = useState(false);
  const [ hoveredCategory, setHoveredCategory ] = useState<Category | null>(null);
  const [ showLeft, setShowLeft ] = useState(false);
  const [ showRight, setShowRight ] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return (): void => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [ categories.length ]);

  const handleCategoryButtonMouseEnter = (category?: Category): void => {
    if (category) {
      setHoveredCategory(category);
    } else {
      setHoveredCategory(null);
    }
  };

  const handleCategoriesMouseLeave = (): void => {
    setIsCategoriesHovered(false);
    setHoveredCategory(null);
  };

  const handleCategoriesMouseEnter = (): void => {
    setIsCategoriesHovered(true);
  };

  const scroll = (direction: 'left' | 'right'): void => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      scrollRef.current.scrollTo({
        left: direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div
      className={ cx(CN) }
      onMouseLeave={ handleCategoriesMouseLeave }
      onMouseEnter={ handleCategoriesMouseEnter }
    >
      <div className={ cx(buttonsContainerClassName) }>
        {showLeft && (
          <button
            type='button'
            aria-label='Scroll left'
            onClick={ (): void => scroll('left') }
            className={ cx(arrowButtonsClassName) }
          >&#8592;</button>
        )}
        <div
          ref={ scrollRef }
          style={ {
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            scrollbarWidth: 'thin'
          } }
        >
          <Button
            variant='text'
            onClick={ (): Promise<boolean> => onCategoryClick('') }
            onMouseOver={ (): void => handleCategoryButtonMouseEnter() }
            style={ { display: 'inline-flex' } }
          >
            All Products
          </Button>
          {categories.map(category => (
            <Button
              variant='text'
              iconEnd={ category.children?.length && (
                <Icon name='arrowDown' width={ 20 } height={ 20 } style={ { marginRight: '-4px' } } />
              ) }
              key={ category.id }
              onMouseOver={ (): void => handleCategoryButtonMouseEnter(category) }
              onClick={ (): Promise<boolean> => onCategoryClick(category.key) }
              style={ { display: 'inline-flex' } }
            >
              { category.name }
            </Button>
          ))}
        </div>
        {showRight && (
          <button
            type='button'
            aria-label='Scroll right'
            onClick={ (): void => scroll('right') }
            className={ cx(arrowButtonsClassName) }
          >&#8594;</button>
        )}
      </div>
      <SubcategoriesNavbar
        isOpen={ isCategoriesHovered }
        setIsOpen={ setIsCategoriesHovered }
        category={ hoveredCategory }
        onCategoryClick={ onCategoryClick }
      />
    </div>
  );
};
